import { useCallback, useEffect, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

// Drawer-state hook. Two channels:
//   - desktop: open/closed persists across reloads via localStorage so
//     the user's last preference survives.
//   - mobile: ephemeral; closes on every route change anyway, so
//     persisting it would just confuse the next visit.
// We pick the right channel based on the current viewport.

const DESKTOP_KEY = "paim-portal-drawer-desktop-open";

interface DrawerHandle {
  isOpen: boolean;
  show: () => void;
  hide: () => void;
  toggle: () => void;
}

// Module-scope subscriber set so multiple components stay in sync
// without a context provider. Lighter than zustand; we have one
// global drawer state per channel.
const desktopSubscribers = new Set<() => void>();
const mobileSubscribers = new Set<() => void>();

let desktopState = ((): boolean => {
  const stored = localStorage.getItem(DESKTOP_KEY);
  return stored === null ? true : stored === "true";
})();
let mobileState = false;

function setDesktop(next: boolean) {
  desktopState = next;
  localStorage.setItem(DESKTOP_KEY, String(next));
  desktopSubscribers.forEach((fn) => fn());
}
function setMobile(next: boolean) {
  mobileState = next;
  mobileSubscribers.forEach((fn) => fn());
}

function useDesktopDrawer(): DrawerHandle {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    desktopSubscribers.add(fn);
    return () => {
      desktopSubscribers.delete(fn);
    };
  }, []);
  return {
    isOpen: desktopState,
    show: useCallback(() => setDesktop(true), []),
    hide: useCallback(() => setDesktop(false), []),
    toggle: useCallback(() => setDesktop(!desktopState), []),
  };
}

function useMobileDrawer(): DrawerHandle {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    mobileSubscribers.add(fn);
    return () => {
      mobileSubscribers.delete(fn);
    };
  }, []);
  return {
    isOpen: mobileState,
    show: useCallback(() => setMobile(true), []),
    hide: useCallback(() => setMobile(false), []),
    toggle: useCallback(() => setMobile(!mobileState), []),
  };
}

export function useDrawer(): DrawerHandle {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const desktop = useDesktopDrawer();
  const mobile = useMobileDrawer();
  return isMobile ? mobile : desktop;
}
